import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { CodeConverterOptions } from './../../../../core/models/options/code-converter-options';
import { CommonDataService } from './../../../../core/services/common-data.service';
import { isNullOrEmpty, getDataScopeByPermission } from 'src/app/core/utils/common-functions';
import { CustomerSignatureSampleStampRequest } from './interface/customer-signature-sample-stamp-request';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { ChangeDetectorRef, Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { LaasOption } from 'src/app/core/models/laas-option';
import { FacilityOptions } from 'src/app/core/models/options/facility-options';
import { DateTableColumn } from 'src/app/core/models/date-table-column';
import { CustomerSignatureSampleStampService } from '../../services/customer-signature-sample-stamp.service';
import { CustomerSignatureSampleStampResponse } from './interface/customer-signature-sample-response';
import { copyFormControl } from 'src/app/core/utils/validation-functions';
import { PageInfo } from 'src/app/core/models/page-info';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { NotificationSubScriptionService } from 'src/app/core/services/notification-sub-scription.service';
import { MultiSelect } from 'primeng/multiselect';
import { UserPreferenceConstants } from '../../../../core/constants/user-preference-constants.constant';

@Component({
  selector: 'laas-customer-signature-sample-stamp',
  templateUrl: './customer-signature-sample-stamp.component.html',
  styleUrls: ['./customer-signature-sample-stamp.component.scss']
})
export class CustomerSignatureSampleStampComponent extends SessionBaseComponent implements OnInit {

  isSearchFormOpen: boolean;

  isLoading: boolean;
  isVisible: boolean;

  searchForm: FormGroup;
  pageInfo: PageInfo;
  sortInfo: { property: string; direction: number } | null;

  imgUrl: string;
  facilityOptions: LaasOption[];
  orgIdOptions: LaasOption[];
  consigneeCompanyOptions: LaasOption[];
  consigneeKeyOptions: LaasOption[];
  addressTypeOptions: LaasOption[];
  brandOptions: LaasOption[] = [];
  internalBrandOptions: LaasOption[] = [];

  selectedRows: CustomerSignatureSampleStampResponse[] = [];
  tableValue: CustomerSignatureSampleStampResponse[];
  tableColumns: DateTableColumn[] = [];
  readonly viewPermissionCode = PermissionConstants.ViewCustomerChopSignSample;
  readonly preferenceKey: string = UserPreferenceConstants.CustomerSignatureSampleStampResponse;

  currentLang: string = this.laasSetting.language;
  @ViewChild('dataTable') dataTable!: DataTableComponent;

  flag:boolean = true;
  searchParams: any;
  consigneeKeyOptionsMapping: LaasOption[];

  constructor(
    injector: Injector,
    private formBuilder: FormBuilder,
    private dropdownOptionsService: DropdownOptionsService,
    private customerSignatureSampleStampService: CustomerSignatureSampleStampService,
    private commonDataService: CommonDataService,
    private notificationSubScriptionService: NotificationSubScriptionService,
    private changeDetector: ChangeDetectorRef,
    ) {
    super(injector);

    const dataTablePreference = this.getDataTablePreference(this.preferenceKey);
    this.sortInfo = isNullOrEmpty(dataTablePreference?.state?.sortField) || isNullOrEmpty(dataTablePreference?.state?.sortOrder) ? null :
      { property: dataTablePreference?.state?.sortField, direction: dataTablePreference?.state?.sortOrder };

    this.isSearchFormOpen = true;

    this.isLoading = false;
    this.isVisible = false;

    this.imgUrl = "";

    this.facilityOptions = [];
    this.orgIdOptions = [];
    this.consigneeCompanyOptions = [];
    this.consigneeKeyOptions = [];
    this.addressTypeOptions = [];

    this.tableValue = [];
    this.tableColumns = this.headerTableTranslation();

    this.searchForm = this.formBuilder.group({
      facility: null,
      orgId: null,
      consigneeCompany: null,
      consigneeKey: null,
      chooseOnlyNone: false,
      brand: null,
      internalBrand: null
    });

    this.pageInfo = {
      pageSize: 5,
      pageIndex: 0,
      totalRecords: 0,
      totalPages: 0,
    };

    this.searchParams = {
      facility: null,
      orgId:null,
      consigneeKey: null,
      consigneeCompany: [],
      chooseOnlyNone: false
    }

    this.consigneeKeyOptionsMapping = [];
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    // permission Codes
    this.permissionCodes = (
      Object.values(this.userProfile.roles).reduce(
        (c: any, i: any) => c.concat(i.permissions),
        []
      ) as any
    ).map((x: any) => {
      return x.permissionCode;
    });

    //rbac to maintain this page
    var canInPage = this.permissionCodes.includes(this.viewPermissionCode);
    if (!canInPage) {
      //go unAuthorize
      this.routeStateService.navigateTo('/Error/unAuthorize');
    }

    this.dropdownOptionsService.getFacilityOptions()
      .subscribe({
        next: result => {
          this.facilityOptions = [...result].map<LaasOption>((facilityOption: FacilityOptions) => {
            return {
              label: `${facilityOption.optionDesc}`,
              value: facilityOption.optionCode
            };
          });
        }
      });

    this.dropdownOptionsService.getLetterOrgOptions()
      .subscribe({
        next: (result: any[]) => {
          this.orgIdOptions = result.map<LaasOption>(val => ({ label: val.legalEntityOUName, value: val.legalEntityOUCode }));
        }
      });

    this.customerSignatureSampleStampService.getConsigneeOptionList("company").subscribe({
      next: (result) => {
        this.consigneeCompanyOptions = result.map(val => ({ label: val, value: val }));
      }
    })

    this.commonDataService.getCodeConverter(this.laasSetting.language, "DropdownList", "TenantAddressType").subscribe({
      next: (result: CodeConverterOptions[]) => {
        this.addressTypeOptions = result.map<LaasOption>(codeConverter => ({ label: codeConverter.codeDesc, value: codeConverter.codeKey }));
      }
    })

    this.notificationSubScriptionService.getCustomerOptions().subscribe({
      next: (options: any[]) => {
        this.consigneeKeyOptionsMapping = options.map(x => ({ label: x.customerCode + ' - ' + x.customerName, value: x.customerCode }));
      }
    });

    this.dropdownOptionsService.getBrandOptions(1).subscribe({
      next: (result) => {
        this.brandOptions = result;
        this.internalBrandOptions = [];
      }
    });
  }

  tablePageChange(event: any) {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.doSearch();
  }

  tableSort(event: any): void {
    this.sortInfo = { property: event.field, direction: event.order };
    this.resetPageInfo();
    this.doSearch();
  }

  getImgUrl(sn: string): string {
    if (isNullOrEmpty(sn)) {
      return "";
    } else {
      return this.customerSignatureSampleStampService.getImgUrl(sn);
    }

  }

  searchBtnClick(): void {
    this.resetPageInfo();
    this.doSearch();
  }

  doSearch() {
    this.isSearchFormOpen = false;
    this.isLoading = true;
    let searchRequest = copyFormControl(this.searchForm, new CustomerSignatureSampleStampRequest());
    searchRequest.pageIndex = this.pageInfo.pageIndex;
    searchRequest.pageSize = this.pageInfo.pageSize;
    searchRequest.sortInfo = isNullOrEmpty(this.sortInfo) ? null : [this.sortInfo];
    searchRequest.userId = this.userProfile.userName;
    searchRequest.userDataScopes = getDataScopeByPermission(this.userProfile, PermissionConstants.ViewCustomerChopSignSample);

    this.customerSignatureSampleStampService.searchCustomerChopSign(searchRequest).subscribe({
      next: (result) => {
        this.tableValue = result.content;
        this.pageInfo.totalRecords = result.totalElements;
        this.isLoading = false;
      }
    });
  }

  resetBtnClick() {
    this.searchForm.reset();
    this.searchForm.get("chooseOnlyNone")?.patchValue(false);
  }

  resetPageInfo() {
    this.pageInfo.pageIndex = 0;
    this.dataTable.setCurrentPage(0);
  }
  // translate table
  headerTableTranslation(): DateTableColumn[] {
    var fields: string[];
    var jsons: string[];
    var tmpcolumn: DateTableColumn[] = [];

    fields = ['orgId', 'consigneeKey', 'type', 'address', 'sn', 'effectiveStartDate',
      'effectiveEndDate', 'updateUser', 'updateDt'];

    jsons = ['CustomerSignatureSampleStamp.Table.Column.OU', 'CustomerSignatureSampleStamp.Table.Column.ConsigneeKey',
      'CustomerSignatureSampleStamp.Table.Column.Type', 'CustomerSignatureSampleStamp.Table.Column.ShipAddress', 'CustomerSignatureSampleStamp.Table.Column.ChopSignSample',
      'CustomerSignatureSampleStamp.Table.Column.EffectiveStartDate', 'CustomerSignatureSampleStamp.Table.Column.EffectiveEndDate'
      , 'CustomerSignatureSampleStamp.Table.Column.UpdateUser', 'CustomerSignatureSampleStamp.Table.Column.UpdateDt'];

    for (let i = 0; i < fields.length; i++) {
      this.translateService.get(jsons[i])
        .subscribe((translation: string) => {
          if (i == 5 || i == 6 || i == 8) {
            tmpcolumn[i] = ({ field: fields[i], header: translation, dataType: 'date', sort: true });
          } else {
            tmpcolumn[i] = ({ field: fields[i], header: translation, sort: true });
          }
        })
    }
    return tmpcolumn;
  }

  showDialog(url: string) {
    this.isVisible = true;
    this.imgUrl = url;
  }

  getCustomerOptions(event: any) {
    const conText: string = event.filter;
    if (conText != null && conText.length > 0 && this.flag) {
      this.flag = false;
      this.notificationSubScriptionService.getCustomerOptionsByFilter(conText).subscribe({
        next: (options: any[]) => {
          this.consigneeKeyOptions = options.map<LaasOption>(x => ({
            label: x.customerCode + ' - ' + x.customerName,
            value: x.customerCode,
          }));
          this.changeDetector.detectChanges();
          this.flag = true;
        }
      });
    }
  }

  async setPageSize(event: any): Promise<void> {
    await super.ngOnInit();
    this.pageInfo.pageSize = event.rows;
  }

  optionsDataOnChange(data: any, type: string): void {
    switch (type) {
      case 'facility':
        this.searchParams.facility = data;
        break;
    }
  }
}
